"use client";

import { useCallback, useEffect, useState, type RefObject } from "react";

/**
 * Whether an element's content overflows it vertically right now. Re-measures when the element or
 * its children resize, so it follows content that loads in after the first paint.
 *
 * @param ref - Ref to the scroll container.
 * @param isMounted - Whether the container is rendered. Pass false while it is not, so the
 *   measurement starts once it appears (a portaled modal mounts a render after its first).
 * @returns True while `scrollHeight` exceeds `clientHeight`.
 */
export function useIsScrollable(ref: RefObject<HTMLElement | null>, isMounted: boolean): boolean {
  // States
  const [isScrollable, setIsScrollable] = useState(false);

  // Handlers
  const measure = useCallback((): void => {
    const element = ref.current;
    if (!element) return;
    setIsScrollable(element.scrollHeight > element.clientHeight);
  }, [ref]);

  // Effects
  useEffect(() => {
    const element = ref.current;
    if (!isMounted || !element) return;
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    Array.from(element.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [ref, measure, isMounted]);

  return isScrollable;
}
